// ============================================================================
// client/fond-papier.ts — LES HUIT FONDS EN PAPIER DÉCOUPÉ
// ----------------------------------------------------------------------------
// Lot 4 de la refonte. C'est ici que se règle le défaut le plus grave de l'audit
// du 28/07 : « le fond fait le même bruit que le jeu ». Les anciens fonds
// étaient du dessin vectoriel lisse, riches en détail, avec des fenêtres
// allumées de la couleur exacte des pièces d'or. On les remplace par des
// DÉCOUPES DE PAPIER qui obéissent à deux lois, et à elles seules :
//
//   1. LE DÉCOR NE PORTE JAMAIS D'OMBRE PORTÉE. C'est ce qui le colle au fond
//      de la page pendant que le terrain et les personnages s'en décollent.
//      La profondeur se lit à l'ombre, pas au niveau de détail.
//   2. LE DÉCOR EST PLAFONNÉ. Deux ou trois aplats par monde, jamais plus
//      clairs ni plus saturés que le jouable. Un fond n'a pas à être vu, il a
//      à être là.
//
// Chaque monde garde son identité par la TEINTE de son papier et une silhouette
// caractéristique, jamais par la quantité de détail.
// ============================================================================

import type { WorldKey } from '../shared/worlds'
import { bruit, papier, rgba, setDetail } from './papier'

type Ctx = CanvasRenderingContext2D

/** Un monde en papier : la teinte de la feuille, puis deux ou trois plans de
 *  découpe, du plus lointain au plus proche. `signature` est le trait qui rend
 *  le monde reconnaissable en une seconde. */
interface FondPapier {
  feuille: string
  plans: readonly [string, string, string]
  encre: string
  signature: 'aucune' | 'ville' | 'stalactites' | 'collines' | 'dunes' | 'sapins' | 'cheminees' | 'recif' | 'manoir'
  reglure: boolean
}

const FONDS: Record<WorldKey, FondPapier> = {
  classique: { feuille: '#241d33', plans: ['#2c2440', '#352c4c', '#3e3458'], encre: '#1a1526',
    signature: 'ville', reglure: true },
  grotte: { feuille: '#221f2c', plans: ['#2a2635', '#332e40', '#3c364b'], encre: '#181620',
    signature: 'stalactites', reglure: false },
  prairie: { feuille: '#b8cdd8', plans: ['#9ab89a', '#86a888', '#749878'], encre: '#3a4a3e',
    signature: 'collines', reglure: true },
  desert: { feuille: '#e8cfa4', plans: ['#dcbc8c', '#cfa876', '#c09460'], encre: '#5a4530',
    signature: 'dunes', reglure: true },
  neige: { feuille: '#c8d8e4', plans: ['#b0c4d4', '#9cb2c4', '#8aa2b6'], encre: '#3e4c58',
    signature: 'sapins', reglure: true },
  usine: { feuille: '#22252e', plans: ['#2a2e38', '#333743', '#3c414e'], encre: '#171a20',
    signature: 'cheminees', reglure: false },
  abysses: { feuille: '#1a3a4a', plans: ['#204657', '#275264', '#2e5e72'], encre: '#0f2630',
    signature: 'recif', reglure: false },
  manoir: { feuille: '#241f30', plans: ['#2b2539', '#332c44', '#3a334f'], encre: '#171320',
    signature: 'manoir', reglure: true },
}

/** Une bande de papier découpée aux ciseaux : le bord n'est jamais droit.
 *  Déterministe (même dentelure à chaque partie), et SANS ombre portée. */
function bande(g: Ctx, W: number, H: number, baseY: number, amp: number, per: number, col: string, graine: number): void {
  g.fillStyle = col
  g.beginPath()
  g.moveTo(0, baseY)
  for (let x = 0; x <= W; x += 14) {
    const y = baseY + Math.sin(x / per + graine) * amp + (bruit(x + graine * 97, graine) - .5) * 6
    g.lineTo(x, y)
  }
  g.lineTo(W, H); g.lineTo(0, H)
  g.closePath(); g.fill()
}

/** Les silhouettes de monde. Toutes sont des DÉCOUPES PLEINES, sans contour ni
 *  détail interne : c'est ce qui les empêche de concurrencer le jeu. */
function signature(g: Ctx, W: number, H: number, f: FondPapier): void {
  const sol = H * .62
  if (f.signature === 'ville') {
    /* la ville : des rectangles pleins. Les fenêtres allumées de l'ancien fond
       avaient exactement la couleur des pièces d'or — c'est le défaut nommé
       dans l'audit, on ne les redessine pas. */
    for (let i = 0; i < 26; i++) {
      const x = (bruit(i, 3) * W) | 0, w = 26 + ((bruit(3, i) * 46) | 0)
      const h = 60 + ((bruit(i * 5, 7) * (H * .34)) | 0)
      g.fillStyle = f.plans[i % 2 === 0 ? 0 : 1]
      g.fillRect(x, sol - h, w, h + 40)
    }
  } else if (f.signature === 'stalactites') {
    g.fillStyle = f.plans[1]
    for (let i = 0; i < 18; i++) {
      const x = (i / 18) * W + (bruit(i, 2) - .5) * 30, w = 24 + ((bruit(2, i) * 34) | 0)
      const h = 40 + ((bruit(i * 3, 5) * 110) | 0)
      g.beginPath(); g.moveTo(x - w / 2, 0); g.lineTo(x, h); g.lineTo(x + w / 2, 0); g.closePath(); g.fill()
    }
  } else if (f.signature === 'sapins') {
    for (let i = 0; i < 20; i++) {
      const x = (bruit(i, 9) * W) | 0, h = 50 + ((bruit(9, i) * 60) | 0), w = h * .5
      g.fillStyle = f.plans[i % 2 ? 1 : 2]
      g.beginPath(); g.moveTo(x, sol - h); g.lineTo(x + w / 2, sol + 10); g.lineTo(x - w / 2, sol + 10); g.closePath(); g.fill()
    }
  } else if (f.signature === 'cheminees') {
    for (let i = 0; i < 9; i++) {
      const x = 40 + i * (W / 9), h = 90 + ((bruit(i, 11) * 120) | 0)
      g.fillStyle = f.plans[1]
      g.fillRect(x, sol - h, 22, h + 40)
      g.fillRect(x - 6, sol - h - 10, 34, 12)
    }
  } else if (f.signature === 'recif') {
    for (let i = 0; i < 14; i++) {
      const x = (bruit(i, 13) * W) | 0, h = 40 + ((bruit(13, i) * 90) | 0)
      g.fillStyle = f.plans[i % 2 ? 1 : 2]
      g.beginPath(); g.moveTo(x, sol + 20)
      g.quadraticCurveTo(x - 14, sol - h * .5, x - 4, sol - h)
      g.quadraticCurveTo(x + 12, sol - h * .6, x + 16, sol + 20); g.closePath(); g.fill()
    }
  } else if (f.signature === 'manoir') {
    const x = W * .62, w = 200, h = 150
    g.fillStyle = f.plans[1]
    g.fillRect(x, sol - h, w, h + 40)
    g.beginPath(); g.moveTo(x - 14, sol - h); g.lineTo(x + w / 2, sol - h - 60); g.lineTo(x + w + 14, sol - h); g.closePath(); g.fill()
    g.fillStyle = f.plans[2]
    for (let i = 0; i < 3; i++) g.fillRect(x + 30 + i * 56, sol - h + 40, 26, 40)
  } else if (f.signature === 'dunes' || f.signature === 'collines') {
    /* rien de plus : les bandes suffisent, elles SONT la signature */
  }
}

/** Peint le fond d'un monde dans un canvas W×H. Appelé une seule fois par monde
 *  (worldBackground le met en cache), donc le coût du papier est sans objet. */
export function fondPapier(g: Ctx, W: number, H: number, world: WorldKey): void {
  const f = FONDS[world]
  setDetail(1)                       // le fond est peint une fois : pleine matière

  papier(g, 0, 0, W, H, f.feuille, 'grain', f.encre)

  if (f.reglure) {                   // la réglure du carnet, et elle seule traverse le fond
    g.strokeStyle = rgba(f.encre, .12); g.lineWidth = 1
    for (let y = 30; y < H; y += 30) { g.beginPath(); g.moveTo(0, y + .5); g.lineTo(W, y + .5); g.stroke() }
  }

  signature(g, W, H, f)              // la silhouette, DERRIÈRE les bandes de sol

  bande(g, W, H, H * .58, 26, 210, f.plans[0], 1)
  bande(g, W, H, H * .70, 20, 150, f.plans[1], 2)
  bande(g, W, H, H * .82, 14, 110, f.plans[2], 3)

  /* Un voile sombre en bas : le terrain se pose dessus, et rien du fond ne doit
     remonter le disputer. C'est la loi n° 2 appliquée à la lettre. */
  const v = g.createLinearGradient(0, H * .5, 0, H)
  v.addColorStop(0, 'rgba(0,0,0,0)'); v.addColorStop(1, rgba(f.encre, .28))
  g.fillStyle = v; g.fillRect(0, H * .5, W, H * .5)
}
